import t from "@/lang/lang";
import { Username } from "@/services/backend/auth/components/username";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Check,
  checkRequiresChildren,
  LastMinuteRiskAnalysis,
} from "@/services/backend/vbs/work-orders/types/last-minute-risk-analysis";
import { useVbsLMRAUnconfirmMutation } from "@/services/backend/vbs/work-orders/api/lmra-api";
import { Button } from "@/shared/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Card, CardContent } from "@/shared/components/ui/card";
import { CheckCircle } from "lucide-react";
import { cn } from "@/shared/lib/utils";

export function ConfirmedLMRAComponent(props: {
  lmra: LastMinuteRiskAnalysis;
  disabled?: boolean;
}) {
  const { lmra, disabled = false } = props;
  const [unconfirm, { isLoading, error }] = useVbsLMRAUnconfirmMutation();

  const onClick = () => {
    if (isLoading) {
      return;
    }

    unconfirm({ id: lmra.id });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <CheckCircle className="mr-4 text-green-700" />
              <span className="mr-1">{t("LMRA wurde durchgeführt von: ")}</span>
              <Username userId={lmra.conductedBy} />
            </div>
            <Button
              variant="outline"
              onClick={onClick}
              size="sm"
              disabled={isLoading || disabled}
            >
              {t("LMRA zurücksetzen")}
            </Button>
          </div>
          <RTKQueryErrorAlert error={error} className="mt-3" />
        </CardContent>
      </Card>
      <CheckListComponent checks={lmra.checks} />
    </div>
  );
}

function resultText(check: Check): string {
  if (!check.checked) return "";
  let text: string;
  if (check.multiple) {
    text = (check.selected ?? [])
      .map((i) => check.options[i])
      .filter(Boolean)
      .join(", ");
  } else {
    text = check.options[check.result] ?? "";
  }
  if (check.withComment && check.comment) {
    text = text !== "" ? `${text} (${check.comment})` : check.comment;
  }
  return text;
}

function CheckListComponent(props: { checks: Check[] }) {
  const { checks } = props;

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("Nr")}</TableHead>
            <TableHead className="w-full">{t("Fragestellung")}</TableHead>
            <TableHead>{t("Ergebnis")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...checks].sort(byNumber).map((check) => (
            <CheckComponent key={check.id} check={check} />
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function byNumber(a: Check, b: Check) {
  return a.number - b.number;
}

function shownChildren(check: Check): Check[] {
  if (!checkRequiresChildren(check)) return [];
  return [...check.children]
    .sort(byNumber)
    .filter((child) => resultText(child) !== "");
}

function CheckComponent(props: { check: Check }) {
  const { check } = props;
  const children = shownChildren(check);
  const rowSpan = children.length + 1;

  return (
    <>
      <TableRow className={cn(children.length > 0 && "border-b-0")}>
        <TableCell className="align-top" rowSpan={rowSpan}>
          {check.number}
        </TableCell>
        <TableCell className="align-top whitespace-normal">
          {check.description}
        </TableCell>
        <TableCell className="align-top" rowSpan={rowSpan}>
          {resultText(check)}
        </TableCell>
      </TableRow>
      {children.map((child) => (
        <ChildCheckComponent key={child.id} check={child} />
      ))}
    </>
  );
}

function ChildCheckComponent(props: { check: Check }) {
  const { check } = props;

  return (
    <TableRow>
      <TableCell className="align-top whitespace-normal">
        <div className="mb-2">
          <span className="mr-4">{check.number}</span>
          {check.description}
        </div>
        <div className="text-muted-foreground text-sm">{resultText(check)}</div>
      </TableCell>
    </TableRow>
  );
}
